// Reagieren.js
// Uebungsart "Reagieren": der Lernende spielt EINE Station, die App spricht
// die anderen. Hier wird die Uebung vorbereitet — Rolle festlegen, eine
// Stoerung wuerfeln und als Zuege einschieben. Geprueft wird in der Ansicht.
//
// Die Stoerungen sind linear: einmal gewuerfelt, fest in der Zugliste, mit
// der Uebung gemerkt (Sitzung: Neuladen zeigt dieselbe). Kein Baum — wer
// falsch antwortet, bekommt "Das stimmt noch nicht." und versucht es wieder.
//
// Wortlaut der Stoerungen: Entscheid des Nutzers, 2026-10-16. Vorerst nur im
// "Funkverkehr, 2 Stationen"; die anderen Ablaeufe laufen ohne Stoerung.
//
// Zusaetzliche Felder an einem Zug:
//   stumm:    true — hier wird nichts gesagt (keine Antwort, auf Empfang
//             bleiben). Die richtige Antwort ist "nichts sagen".
//   gestoert: true — der Spruch kommt unverstaendlich an; die Ansicht
//             verdeckt den Inhalt.
//
// zurueck: { ...uebung, art: 'reagieren', kontext, zuege,
//            reagieren: { ich, stoerung } }
//   ich = die Rolle (aus schema.rollen), die der Lernende spricht

import { inventar } from '@/data/daten'

function zufall(liste) {
  return liste[Math.floor(Math.random() * liste.length)]
}

// rolle: 'rufer' | 'gegenstation' | 'zufall' (alles andere zaehlt als zufall)
export function bereiteReagierenVor(uebung, rolle) {
  const rufer = uebung.zuege[0].spricht
  const andere = [...new Set(uebung.zuege.map(z => z.spricht))].filter(r => r !== rufer)
  const wahl = rolle === 'rufer' || rolle === 'gegenstation' ? rolle : zufall(['rufer', 'gegenstation'])
  const ich = wahl === 'rufer' ? rufer : zufall(andere)

  // "keine" ist eine der Moeglichkeiten: auch der normale Ablauf will geuebt sein.
  const tabelle = uebung.kategorie !== 'funkverkehr-2' ? {}
    : wahl === 'rufer' ? alsRufer : alsGegenstation
  const stoerung = zufall(['keine', ...Object.keys(tabelle)])
  const zuege = stoerung === 'keine' ? uebung.zuege : tabelle[stoerung](uebung)

  // Die Gegenstation kennt die Meldung nicht im Voraus — sie hoert sie erst
  // (oder eben nicht).
  let kontext = uebung.kontext
  if (wahl === 'gegenstation' && uebung.bindung.meldung) {
    kontext = kontext.replace(': ' + uebung.bindung.meldung.funktext, '')
  }
  return {
    ...uebung,
    art: 'reagieren',
    kontext: kontext + ' Du bist ' + uebung.bindung[ich].rufname + '.',
    zuege: nummeriert(zuege),
    reagieren: { ich, stoerung }
  }
}

// Ein eingeschobener Zug. "texte" sind die Bausteine in richtiger Reihenfolge.
function zug(uebung, spricht, texte, extra = {}) {
  return {
    spricht,
    station: uebung.bindung[spricht],
    teile: texte.map(text => ({ text })),
    ablenker: [],
    ...extra
  }
}

// Nach dem Einschieben: nr und ids neu, damit sie in der Uebung eindeutig sind.
function nummeriert(zuege) {
  return zuege.map((z, n) => ({
    ...z,
    nr: n,
    teile: z.teile.map((t, i) => ({ ...t, id: 'z' + n + 't' + i }))
  }))
}

// ─── Funkverkehr, 2 Stationen ─────────────────────────────────
// Zuege: [0] Absender sendet die Meldung, [1] Empfaenger quittiert,
// [2] Absender "richtig Schluss".

function namen(uebung) {
  const b = uebung.bindung
  return { A: b.absender.rufname, E: b.empfaenger.rufname, M: b.meldung.funktext }
}

// Der Lernende ist der Absender: die Gegenstation macht Schwierigkeiten.
const alsRufer = {
  nichtVerstanden(u) {
    const { A, E, M } = namen(u)
    const [z0, z1, z2] = u.zuege
    return [z0,
      zug(u, 'empfaenger', [E, 'nicht verstanden', 'wiederholen', 'antworten']),
      zug(u, 'absender', [A, 'verstanden', 'ich wiederhole', M, 'antworten']),
      z1, z2]
  },

  warten(u) {
    const { A, E, M } = namen(u)
    const [z0, , z2] = u.zuege
    return [z0,
      zug(u, 'empfaenger', [E, 'verstanden', 'bitte warten']),
      zug(u, 'absender', [], { stumm: true }),
      zug(u, 'empfaenger', ['An ' + A, 'von ' + E, 'verstanden', M, 'antworten']),
      z2]
  },

  keineAntwort(u) {
    const [z0, z1, z2] = u.zuege
    return [z0,
      zug(u, 'empfaenger', [], { stumm: true }),
      z0, z1, z2]
  },

  falschQuittiert(u) {
    const { A, E, M } = namen(u)
    const [z0, z1, z2] = u.zuege
    // Verhoert wird eine andere Meldung, die dieselbe Station senden koennte.
    const andere = inventar.meldungen.filter(m => m.funktext !== M)
    const passende = andere.filter(m => m.von.includes(u.bindung.absender.id))
    const falsch = zufall(passende.length ? passende : andere).funktext
    return [z0,
      zug(u, 'empfaenger', [E, 'verstanden', falsch, 'antworten']),
      zug(u, 'absender', [A, 'falsch ich wiederhole', M, 'antworten']),
      z1, z2]
  }
}

// Der Lernende ist der Empfaenger: die Meldung kommt unverstaendlich an.
const alsGegenstation = {
  nichtVerstanden(u) {
    const { A, E, M } = namen(u)
    const [z0, z1, z2] = u.zuege
    return [{ ...z0, gestoert: true },
      zug(u, 'empfaenger', [E, 'nicht verstanden', 'wiederholen', 'antworten']),
      zug(u, 'absender', [A, 'verstanden', 'ich wiederhole', M, 'antworten']),
      z1, z2]
  }
}
